// SQLEditor.jsx
import React, { useState } from 'react';
import { FaCode, FaDatabase, FaTable } from 'react-icons/fa';
import { TbDatabaseEdit, TbDatabaseX, TbDatabaseExclamation } from "react-icons/tb";
import { Tabs, Tab, Box, Collapse, IconButton } from '@mui/material';
import TabPanel from '@mui/lab/TabPanel';
import TabContext from '@mui/lab/TabContext';
import { ChevronRight, Close, KeyboardArrowDown, PlayArrow } from '@mui/icons-material';
import Styles from './DBViewer.module.scss';
import { useEffect } from 'react';
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';
import Editor from '@monaco-editor/react'
import { useEditor } from 'views/CodeEditor/contexts';

const DBViewer = ({ setShowDBViewer, dbPath }) => {

    const { socket, setUserSqlInput } = useEditor();

    const [schema, setSchema] = useState({});
    const [isConnected, setIsConnected] = useState(false);
    const [dbStatus, setDbStatus] = useState('');

    const [activeTab, setActiveTab] = useState('tables');
    const [query, setQuery] = useState('SELECT * FROM employee;');
    const [queryResult, setQueryResult] = useState(null);
    const [expandedTables, setExpandedTables] = useState({});

    useEffect(() => {
        socket.on('connect', () => {
            setIsConnected(true);
            // Connect to default database
            socket.emit('db:connect', {
                path: dbPath,
                initialize: false
            });
        });

        socket.on('db:schema', (dbSchema) => {
            const formattedSchema = {};
            dbSchema.forEach(table => {
                formattedSchema[table.tableName] = {
                    columns: table.columns.map(col => col.name),
                    columnTypes: table.columns.map(col => ({
                        name: col.name,
                        type: col.type,
                        notNull: col.notNull,
                        defaultValue: col.defaultValue,
                        isPrimaryKey: col.isPrimaryKey
                    })),
                    data: []
                };
            });
            setSchema(formattedSchema);
        });

        socket.on('query:result', (result) => {
            if (result.type === 'select') {
                setQueryResult({
                    columns: result.columns,
                    data: result.data
                });
            } else {
                setQueryResult({
                    message: result.message,
                    changes: result.changes,
                    lastID: result.lastID
                });
            }
        });

        socket.on('query:error', (error) => {
            setQueryResult({ error });
        });

        return () => {
            socket.close();
        }

    }, [])

    const executeQuery = () => {
        setUserSqlInput(query);
        if (socket && isConnected) {
            socket.emit('query:execute', query);
        }
    };

    const toggleTableExpand = (tableName) => {
        setExpandedTables(prev => ({
            ...prev,
            [tableName]: !prev[tableName]
        }));
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleEditorChange = (value) => {
        setUserSqlInput(value)
        setQuery(value);
    };

    const TableView = ({ columns, data, message }) => {
        if (message) {
            return (
                <div className={Styles.queryMessage}>
                    <TbDatabaseEdit className={Styles.queryMessageIcon} />
                    <p className={Styles.queryMessageText}>{message}</p>
                </div>
            );
        }

        if (!data || data.length === 0) {
            return (
                <div className={Styles.noRecords}>
                    <TbDatabaseX className={Styles.noRecordsIcon} />
                    <p className={Styles.noRecordsText}>No records found</p>
                </div>
            );
        }

        return (
            <div className={Styles.tableContainer}>
                <table>
                    <thead>
                        <tr>
                            {columns.map((col, i) => (
                                <th title={col} key={i}>{col}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row, i) => (
                            <tr key={i}>
                                {row.map((cell, j) => (
                                    <td title={cell} key={j}>{cell}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )
    }

    return (
        <div className={Styles.sqlEditor}>
            <div className={Styles.editorContainer}>
                <header className={Styles.header}>
                    <h1>SQL Database Viewer</h1>
                    <IconButton title='Close' onClick={() => { setShowDBViewer('showDbViewer', false) }}>
                        <Close fontSize='medium' sx={{ color: 'white' }} />
                    </IconButton>
                </header>

                <div className={Styles.mainContent}>
                    <aside className={Styles.sidebar}>
                        <div className={Styles.sidebarContent}>
                            <div className={Styles.databaseHeader}>
                                <FaDatabase size={20} />
                                <span>Database</span>
                            </div>

                            <div className={Styles.tableList}>
                                {Object.entries(schema).map(([tableName, tableData]) => (
                                    <div key={tableName} className={Styles.tableItemContainer}>
                                        <div
                                            className={Styles.tableItem}
                                            onClick={() => toggleTableExpand(tableName)}
                                        >
                                            {expandedTables[tableName] ? (
                                                <KeyboardArrowDown size={16} />
                                            ) : (
                                                <ChevronRight size={16} />
                                            )}
                                            <FaTable size={16} />
                                            <span title={tableName} className={Styles.tableName}>{tableName}</span>
                                        </div>
                                        <Collapse in={expandedTables[tableName]}>
                                            <div className={Styles.columnList}>
                                                {tableData.columnTypes.map((column, index) => (
                                                    <div key={index} className={Styles.columnItem}>
                                                        <span title={column.name} className={Styles.columnName}>{column.name}</span>
                                                        <span className={Styles.columnType}>{column.type}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </Collapse>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </aside>

                    <main className={Styles.mainPanel}>
                        <TabContext value={activeTab}>
                            <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: '0.5em' }}>
                                <Tabs
                                    value={activeTab}
                                    onChange={handleTabChange}
                                    sx={{
                                        '& .MuiTab-root': {
                                            textTransform: 'none',
                                            minHeight: '48px',
                                        }
                                    }}
                                >
                                    <Tab
                                        icon={<FaTable size={16} />}
                                        iconPosition="start"
                                        label="Tables"
                                        value="tables"
                                    />
                                    <Tab
                                        icon={<FaCode size={16} />}
                                        iconPosition="start"
                                        label="Query Editor"
                                        value="query"
                                    />
                                </Tabs>
                                <AivagamButton
                                    onClick={executeQuery}
                                    disabled={!query}
                                    buttonText={<span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Execute <PlayArrow fontSize='small' sx={{ ml: '0.25em' }} /> </span>}
                                    classStyles={{ boxShadow: '0 3px 6px rgba(0, 0, 0, 0.3)' }}
                                />
                            </Box>

                            <TabPanel value="tables" className={Styles.tabPanel}>
                                {queryResult && !queryResult.error && (
                                    <TableView
                                        columns={queryResult.columns}
                                        data={queryResult.data}
                                    />
                                )}
                            </TabPanel>

                            <TabPanel value="query" className={Styles.tabPanel}>
                                <div className={Styles.queryEditor}>
                                    <div className={Styles.editorWrapper}>
                                        <Editor
                                            height="200px"
                                            width='60vw'
                                            defaultLanguage="sql"
                                            value={query}
                                            onChange={handleEditorChange}
                                            theme="vs-light"
                                            options={{
                                                minimap: { enabled: false },
                                                fontSize: 14,
                                                formatOnPaste: true,
                                                formatOnType: true,
                                                scrollBeyondLastLine: false,
                                                automaticLayout: true,
                                                suggestOnTriggerCharacters: true,
                                                lineNumbers: "on",
                                                wordWrap: "on",
                                            }}
                                        />
                                    </div>
                                    {queryResult && (
                                        <div className={Styles.results}>
                                            {queryResult.error ? (
                                                <div className={Styles.errorMessage}>
                                                    <TbDatabaseExclamation className={Styles.errorIcon} />
                                                    <p className={Styles.errorText}>{queryResult.error}</p>
                                                </div>
                                            ) : (
                                                <TableView
                                                    columns={queryResult.columns}
                                                    data={queryResult.data}
                                                    message={queryResult.message}
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                            </TabPanel>
                        </TabContext>
                    </main>
                </div>
            </div>
        </div>
    );
};

export default DBViewer;